import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { OpportunityCard } from "@/components/directory/OpportunityCard";
import { Card, CardContent } from "@/components/ui/card";
import { Database } from "@/integrations/supabase/types";

type OpportunityType = Database['public']['Enums']['opportunity_type'];

interface OpportunityListProps {
  selectedType: OpportunityType | 'all';
}

export const OpportunityList = ({ selectedType }: OpportunityListProps) => {
  const { data: opportunities, isLoading, error } = useQuery({
    queryKey: ["opportunities", selectedType],
    queryFn: async () => {
      console.log("Fetching opportunities with type:", selectedType);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.log("No authenticated user found");
        throw new Error("Authentication required");
      }

      const { data: subscription } = await supabase
        .from("user_subscriptions")
        .select("tier")
        .eq("user_id", user.id)
        .maybeSingle();

      console.log("User subscription:", subscription);

      let query = supabase
        .from("opportunities")
        .select(`
          *,
          profiles:created_by (
            id,
            full_name,
            avatar_url
          )
        `)
        .eq('status', 'published')
        .order("created_at", { ascending: false });

      // Apply type filter if not 'all'
      if (selectedType !== 'all') {
        query = query.eq('type', selectedType);
      }

      if (subscription?.tier !== "premium") {
        console.log("User does not have premium subscription");
        query = query.limit(3);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="bg-red-50">
        <CardContent className="pt-6">
          <p className="text-red-600">Error loading opportunities. Please try again later.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 mt-6">
      {opportunities?.map((opportunity) => (
        <OpportunityCard
          key={opportunity.id}
          id={opportunity.id}
          title={opportunity.title}
          description={opportunity.description}
          type={opportunity.type}
          location={opportunity.location}
          remoteAllowed={opportunity.remote_allowed || false}
          compensation={opportunity.compensation}
          deadline={opportunity.deadline}
          requirements={opportunity.requirements}
          createdBy={opportunity.profiles ? {
            id: opportunity.profiles.id,
            fullName: opportunity.profiles.full_name,
            avatarUrl: opportunity.profiles.avatar_url,
          } : null}
        />
      ))}
    </div>
  );
};